import { scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime/environment";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@t3tools/client-runtime/state/runtime";
import type { EnvironmentId, ScopedThreadRef, ThreadId, VcsRef } from "@t3tools/contracts";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { type DraftId, useComposerDraftStore } from "../composerDraftStore";
import { parsePullRequestReference } from "../pullRequestReference";
import {
  readThreadShell,
  useProject,
  useThread,
  useThreadShellsForProjectRefs,
} from "../state/entities";
import { usePaginatedBranches } from "../state/queries";
import { useEnvironmentQuery } from "../state/query";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";
import { vcsEnvironment } from "../state/vcs";
import { getSourceControlPresentation } from "../sourceControlPresentation";
import {
  deriveLocalBranchNameFromRemoteRef,
  type EnvironmentOption,
  type EnvMode,
  resolveBranchSelectionTarget,
  resolveBranchToolbarPrBranch,
  resolveBranchToolbarValue,
  resolveBranchTriggerLabel,
  resolveDraftEnvModeAfterBranchChange,
  resolveEffectiveEnvMode,
  resolvePreviousWorktreeLabel,
  resolvePreviousWorktreeSeed,
  shouldIncludeBranchPickerItem,
  shouldShowEnvironmentIndicator,
} from "./BranchToolbar.logic";
import {
  prStatusIndicator,
  resolveThreadPr,
  type PrStatusIndicator,
  type ThreadPr,
} from "./ThreadStatusIndicators";
import { stackedThreadToast, toastManager } from "./ui/toast";

export interface RunContextControllerOptions {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly draftId?: DraftId;
  readonly envLocked: boolean;
  readonly effectiveEnvModeOverride?: EnvMode;
  readonly activeThreadBranchOverride?: string | null;
  readonly onActiveThreadBranchOverrideChange?: (branch: string | null) => void;
  readonly startFromOrigin: boolean;
  readonly onEnvModeChange: (mode: EnvMode) => void;
  readonly onStartFromOriginChange: (startFromOrigin: boolean) => void;
  readonly availableEnvironments?: readonly EnvironmentOption[];
  readonly onEnvironmentChange?: (environmentId: EnvironmentId) => void;
  readonly onCheckoutPullRequestRequest?: (reference: string) => void;
  readonly onComposerFocusRequest?: () => void;
}

export interface RunContextBranchPresentation {
  readonly activeProjectCwd: string | null;
  readonly branchPr: ThreadPr | null;
  readonly branchPrStatus: PrStatusIndicator | null;
  readonly branchPrTooltip: string;
  readonly branchQuery: string;
  readonly branchStatusText: string | null;
  readonly branchByName: ReadonlyMap<string, VcsRef>;
  readonly branchPickerItems: readonly string[];
  readonly checkoutPullRequestItemValue: string | null;
  readonly createBranchItemValue: string | null;
  readonly filteredBranchPickerItems: readonly string[];
  readonly hasNextPage: boolean;
  readonly isActionPending: boolean;
  readonly isFetchingNextPage: boolean;
  readonly isInitialLoadPending: boolean;
  readonly isMenuOpen: boolean;
  readonly menuOwner: string | null;
  readonly isSelectingWorktreeBase: boolean;
  readonly prReference: string | null;
  readonly resolvedActiveBranch: string | null;
  readonly sourceControlPresentation: ReturnType<typeof getSourceControlPresentation>;
  readonly startFromOriginSwitchId: string;
  readonly triggerLabel: string;
  readonly createRef: (name: string) => void;
  readonly loadNextPage: () => void;
  readonly requestCheckoutPullRequest: () => void;
  readonly selectRef: (ref: VcsRef) => void;
  readonly setBranchQuery: (query: string) => void;
  readonly setMenuOpen: (owner: string, open: boolean) => void;
}

export interface RunContextController {
  readonly activeWorktreePath: string | null;
  readonly availableEnvironments: readonly EnvironmentOption[] | undefined;
  readonly branch: RunContextBranchPresentation;
  readonly effectiveEnvMode: EnvMode;
  readonly environmentId: EnvironmentId;
  readonly envLocked: boolean;
  readonly envModeLocked: boolean;
  readonly hasActiveContext: boolean;
  readonly previousWorktreeLabel: string | null;
  readonly showEnvironmentIndicator: boolean;
  readonly showEnvironmentPicker: boolean;
  readonly startFromOrigin: boolean;
  readonly activeThreadBranch: string | null;
  readonly onEnvironmentChange: ((environmentId: EnvironmentId) => void) | undefined;
  readonly onEnvModeChange: (mode: EnvMode) => void;
  readonly onStartFromOriginChange: (startFromOrigin: boolean) => void;
  readonly onUsePreviousWorktree: () => void;
}

interface BranchPresentationState {
  readonly scopeKey: string;
  readonly menuOpen: boolean;
  readonly menuOwner: string | null;
  readonly query: string;
}

interface OptimisticBranchState {
  readonly scopeKey: string;
  readonly branch: string | null;
}

interface BranchActionOrigin {
  readonly threadRef: ScopedThreadRef;
  readonly draftId?: DraftId;
}

const RunContextControllerContext = createContext<RunContextController | null>(null);

function toBranchActionErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "An error occurred.";
}

export function runContextControllerScopeKey(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly draftId?: DraftId;
  readonly projectCwd: string | null;
  readonly branchCwd: string | null;
}): string {
  return JSON.stringify([
    input.environmentId,
    input.threadId,
    input.draftId ?? null,
    input.projectCwd,
    input.branchCwd,
  ]);
}

function useCreateRunContextController(options: RunContextControllerOptions): RunContextController {
  const {
    environmentId,
    threadId,
    draftId,
    envLocked,
    effectiveEnvModeOverride,
    activeThreadBranchOverride,
    onActiveThreadBranchOverrideChange,
    startFromOrigin,
    onEnvModeChange,
    onStartFromOriginChange,
    availableEnvironments,
    onEnvironmentChange,
    onCheckoutPullRequestRequest,
    onComposerFocusRequest,
  } = options;
  const stopThreadSession = useAtomCommand(threadEnvironment.stopSession, "thread session stop");
  const updateThreadMetadata = useAtomCommand(
    threadEnvironment.updateMetadata,
    "thread metadata update",
  );
  const switchRef = useAtomCommand(vcsEnvironment.switchRef, { reportFailure: false });
  const createRefMutation = useAtomCommand(vcsEnvironment.createRef, { reportFailure: false });
  const startFromOriginSwitchId = useId();
  const threadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const draftThread = useComposerDraftStore((store) =>
    draftId ? store.getDraftSession(draftId) : store.getDraftThreadByRef(threadRef),
  );
  const serverThread = useThread(threadRef, { waitForShell: draftThread !== null });
  const setDraftThreadContext = useComposerDraftStore((store) => store.setDraftThreadContext);
  const activeProjectRef = serverThread
    ? scopeProjectRef(serverThread.environmentId, serverThread.projectId)
    : draftThread
      ? scopeProjectRef(draftThread.environmentId, draftThread.projectId)
      : null;
  const activeProject = useProject(activeProjectRef);
  const activeProjectCwd = activeProject?.workspaceRoot ?? null;
  const activeWorktreePath = serverThread?.worktreePath ?? draftThread?.worktreePath ?? null;
  const hasServerThread = serverThread !== null;
  const effectiveEnvMode =
    effectiveEnvModeOverride ??
    resolveEffectiveEnvMode({
      activeWorktreePath,
      hasServerThread,
      draftThreadEnvMode: draftThread?.envMode,
    });
  const activeThreadBranch =
    activeThreadBranchOverride !== undefined
      ? activeThreadBranchOverride
      : (serverThread?.branch ?? draftThread?.branch ?? null);
  const branchCwd = activeWorktreePath ?? activeProjectCwd;
  const controllerScopeKey = runContextControllerScopeKey({
    environmentId,
    threadId,
    ...(draftId ? { draftId } : {}),
    projectCwd: activeProjectCwd,
    branchCwd,
  });
  const branchActionOrigin = useMemo<BranchActionOrigin>(
    () => ({
      threadRef,
      ...(draftId ? { draftId } : {}),
    }),
    [draftId, threadRef],
  );
  const activeThreadRef = useRef(threadRef);
  activeThreadRef.current = threadRef;
  const activeBranchOverrideChangeRef = useRef(onActiveThreadBranchOverrideChange);
  activeBranchOverrideChangeRef.current = onActiveThreadBranchOverrideChange;

  const setThreadBranchForOrigin = useCallback(
    (origin: BranchActionOrigin, branch: string | null, worktreePath: string | null) => {
      const currentServerThread = readThreadShell(origin.threadRef);
      if (currentServerThread) {
        if (currentServerThread.session && worktreePath !== currentServerThread.worktreePath) {
          void stopThreadSession({
            environmentId: origin.threadRef.environmentId,
            input: { threadId: origin.threadRef.threadId },
          });
        }
        void updateThreadMetadata({
          environmentId: origin.threadRef.environmentId,
          input: {
            threadId: origin.threadRef.threadId,
            branch,
            worktreePath,
          },
        });
        const activeRef = activeThreadRef.current;
        if (
          activeRef.environmentId === origin.threadRef.environmentId &&
          activeRef.threadId === origin.threadRef.threadId
        ) {
          activeBranchOverrideChangeRef.current?.(branch);
        }
        return;
      }

      const draftStore = useComposerDraftStore.getState();
      const currentDraft = origin.draftId
        ? draftStore.getDraftSession(origin.draftId)
        : draftStore.getDraftThreadByRef(origin.threadRef);
      if (
        !currentDraft ||
        currentDraft.environmentId !== origin.threadRef.environmentId ||
        currentDraft.threadId !== origin.threadRef.threadId
      ) {
        return;
      }
      const currentDraftEnvMode = resolveEffectiveEnvMode({
        activeWorktreePath: currentDraft.worktreePath,
        hasServerThread: false,
        draftThreadEnvMode: currentDraft.envMode,
      });
      const nextDraftEnvMode = resolveDraftEnvModeAfterBranchChange({
        nextWorktreePath: worktreePath,
        currentWorktreePath: currentDraft.worktreePath,
        effectiveEnvMode: currentDraftEnvMode,
      });
      draftStore.setDraftThreadContext(origin.draftId ?? origin.threadRef, {
        branch,
        worktreePath,
        envMode: nextDraftEnvMode,
        projectRef: scopeProjectRef(currentDraft.environmentId, currentDraft.projectId),
      });
    },
    [stopThreadSession, updateThreadMetadata],
  );

  const setThreadBranch = useCallback(
    (branch: string | null, worktreePath: string | null) => {
      setThreadBranchForOrigin(branchActionOrigin, branch, worktreePath);
    },
    [branchActionOrigin, setThreadBranchForOrigin],
  );

  const envModeLocked = envLocked || (serverThread !== null && activeWorktreePath !== null);
  const canUsePreviousWorktree = draftThread !== null && serverThread === null && !envModeLocked;
  const projectRefsForWorktreeLookup = useMemo(
    () => (canUsePreviousWorktree && activeProjectRef ? [activeProjectRef] : []),
    [activeProjectRef, canUsePreviousWorktree],
  );
  const projectThreads = useThreadShellsForProjectRefs(projectRefsForWorktreeLookup);
  const previousWorktreeSeed = useMemo(
    () =>
      canUsePreviousWorktree
        ? resolvePreviousWorktreeSeed({
            threads: projectThreads,
            currentWorktreePath: activeWorktreePath,
          })
        : null,
    [activeWorktreePath, canUsePreviousWorktree, projectThreads],
  );
  const previousWorktreeLabel = previousWorktreeSeed
    ? resolvePreviousWorktreeLabel(previousWorktreeSeed)
    : null;
  const onUsePreviousWorktree = useCallback(() => {
    if (!previousWorktreeSeed || !activeProjectRef) return;
    setDraftThreadContext(draftId ?? threadRef, {
      branch: previousWorktreeSeed.branch,
      worktreePath: previousWorktreeSeed.worktreePath,
      envMode: "worktree",
      projectRef: activeProjectRef,
    });
  }, [activeProjectRef, draftId, previousWorktreeSeed, setDraftThreadContext, threadRef]);
  const showEnvironmentPicker = Boolean(
    availableEnvironments && availableEnvironments.length > 1 && onEnvironmentChange,
  );
  const activeEnvironmentOption =
    availableEnvironments?.find((environment) => environment.environmentId === environmentId) ??
    null;
  const showEnvironmentIndicator = shouldShowEnvironmentIndicator({
    activeEnvironment: activeEnvironmentOption,
    canPickEnvironment: showEnvironmentPicker,
  });

  const [branchPresentationState, setBranchPresentationState] = useState<BranchPresentationState>({
    scopeKey: controllerScopeKey,
    menuOpen: false,
    menuOwner: null,
    query: "",
  });
  if (branchPresentationState.scopeKey !== controllerScopeKey) {
    setBranchPresentationState({
      scopeKey: controllerScopeKey,
      menuOpen: false,
      menuOwner: null,
      query: "",
    });
  }
  const activeBranchPresentationState =
    branchPresentationState.scopeKey === controllerScopeKey
      ? branchPresentationState
      : { scopeKey: controllerScopeKey, menuOpen: false, menuOwner: null, query: "" };
  const setBranchPresentationPatch = useCallback(
    (patch: Partial<Omit<BranchPresentationState, "scopeKey">>) => {
      setBranchPresentationState((current) => ({
        ...(current.scopeKey === controllerScopeKey
          ? current
          : { scopeKey: controllerScopeKey, menuOpen: false, menuOwner: null, query: "" }),
        ...patch,
      }));
    },
    [controllerScopeKey],
  );
  const deferredBranchQuery = useDeferredValue(activeBranchPresentationState.query);
  const trimmedBranchQuery = activeBranchPresentationState.query.trim();
  const deferredTrimmedBranchQuery = deferredBranchQuery.trim();
  const branchStatusQuery = useEnvironmentQuery(
    branchCwd === null
      ? null
      : vcsEnvironment.status({
          environmentId,
          input: { cwd: branchCwd },
        }),
  );
  const branchRefTarget = useMemo(
    () => ({
      environmentId,
      cwd: branchCwd,
      query: deferredTrimmedBranchQuery,
    }),
    [branchCwd, deferredTrimmedBranchQuery, environmentId],
  );
  const branchRefState = usePaginatedBranches(branchRefTarget);
  const refs = branchRefState.refs;
  const hasNextPage =
    branchRefState.data?.nextCursor !== null && branchRefState.data?.nextCursor !== undefined;
  const currentGitBranch =
    branchStatusQuery.data?.refName ?? refs.find((refName) => refName.current)?.name ?? null;
  const canonicalActiveBranch = resolveBranchToolbarValue({
    envMode: effectiveEnvMode,
    activeWorktreePath,
    activeThreadBranch,
    currentGitBranch,
  });
  const [optimisticBranch, setOptimisticBranch] = useState<OptimisticBranchState | null>(null);
  const resolvedActiveBranch =
    optimisticBranch?.scopeKey === controllerScopeKey
      ? optimisticBranch.branch
      : canonicalActiveBranch;
  const branchNames = useMemo(() => refs.map((refName) => refName.name), [refs]);
  const branchByName = useMemo(
    () => new Map(refs.map((refName) => [refName.name, refName] as const)),
    [refs],
  );
  const listedActiveBranch =
    resolvedActiveBranch === null ? null : (branchByName.get(resolvedActiveBranch) ?? null);
  const activeBranchRefQuery = useEnvironmentQuery(
    branchCwd !== null && resolvedActiveBranch !== null
      ? vcsEnvironment.listRefs({
          environmentId,
          input: {
            cwd: branchCwd,
            query: resolvedActiveBranch,
            limit: 10,
          },
        })
      : null,
  );
  const queriedActiveBranch = activeBranchRefQuery.data?.refs.find(
    (refName) => refName.name === resolvedActiveBranch,
  );
  const resolvedActiveBranchIsRemote =
    listedActiveBranch !== null
      ? listedActiveBranch.isRemote === true
      : queriedActiveBranch
        ? queriedActiveBranch.isRemote === true
        : null;
  const isSelectingWorktreeBase =
    effectiveEnvMode === "worktree" && !envLocked && !activeWorktreePath;
  const prReference = parsePullRequestReference(trimmedBranchQuery);
  const checkoutPullRequestItemValue =
    prReference && onCheckoutPullRequestRequest ? `__checkout_pull_request__:${prReference}` : null;
  const canCreateBranch = !isSelectingWorktreeBase && trimmedBranchQuery.length > 0;
  const hasExactBranchMatch = branchByName.has(trimmedBranchQuery);
  const createBranchItemValue = canCreateBranch
    ? `__create_new_branch__:${trimmedBranchQuery}`
    : null;
  const branchPickerItems = useMemo(() => {
    const items = [...branchNames];
    if (createBranchItemValue && !hasExactBranchMatch) {
      items.push(createBranchItemValue);
    }
    if (checkoutPullRequestItemValue) {
      items.unshift(checkoutPullRequestItemValue);
    }
    return items;
  }, [branchNames, checkoutPullRequestItemValue, createBranchItemValue, hasExactBranchMatch]);
  const normalizedDeferredBranchQuery = deferredTrimmedBranchQuery.toLowerCase();
  const filteredBranchPickerItems = useMemo(
    () =>
      normalizedDeferredBranchQuery.length === 0
        ? branchPickerItems
        : branchPickerItems.filter((itemValue) =>
            shouldIncludeBranchPickerItem({
              itemValue,
              normalizedQuery: normalizedDeferredBranchQuery,
              createBranchItemValue,
              checkoutPullRequestItemValue,
            }),
          ),
    [
      branchPickerItems,
      checkoutPullRequestItemValue,
      createBranchItemValue,
      normalizedDeferredBranchQuery,
    ],
  );
  const [pendingActionScopes, setPendingActionScopes] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const isActionPending = pendingActionScopes.has(controllerScopeKey);
  const runBranchAction = useCallback(
    (action: (actionScopeKey: string) => Promise<void>) => {
      const actionScopeKey = controllerScopeKey;
      setPendingActionScopes((current) => new Set(current).add(actionScopeKey));
      void action(actionScopeKey).finally(() => {
        branchRefState.refresh();
        branchStatusQuery.refresh();
        setPendingActionScopes((current) => {
          if (!current.has(actionScopeKey)) return current;
          const next = new Set(current);
          next.delete(actionScopeKey);
          return next;
        });
        setOptimisticBranch((current) => (current?.scopeKey === actionScopeKey ? null : current));
      });
    },
    [branchRefState.refresh, branchStatusQuery.refresh, controllerScopeKey],
  );
  const selectRef = useCallback(
    (refName: VcsRef) => {
      if (!branchCwd || !activeProjectCwd || isActionPending) return;
      if (isSelectingWorktreeBase) {
        setThreadBranch(refName.name, null);
        setBranchPresentationPatch({ menuOpen: false, menuOwner: null });
        onComposerFocusRequest?.();
        return;
      }
      const selectionTarget = resolveBranchSelectionTarget({
        activeProjectCwd,
        activeWorktreePath,
        refName,
      });
      if (selectionTarget.reuseExistingWorktree) {
        setThreadBranch(refName.name, selectionTarget.nextWorktreePath);
        setBranchPresentationPatch({ menuOpen: false, menuOwner: null });
        onComposerFocusRequest?.();
        return;
      }
      const selectedBranchName = refName.isRemote
        ? deriveLocalBranchNameFromRemoteRef(refName.name)
        : refName.name;
      const actionOrigin = branchActionOrigin;
      setBranchPresentationPatch({ menuOpen: false, menuOwner: null });
      onComposerFocusRequest?.();
      runBranchAction(async (actionScopeKey) => {
        const previousBranch = resolvedActiveBranch;
        setOptimisticBranch({ scopeKey: actionScopeKey, branch: selectedBranchName });
        const checkoutResult = await switchRef({
          environmentId,
          input: {
            cwd: selectionTarget.checkoutCwd,
            refName: refName.name,
          },
        });
        if (checkoutResult._tag === "Success") {
          const nextBranchName = refName.isRemote
            ? (checkoutResult.value.refName ?? selectedBranchName)
            : selectedBranchName;
          setOptimisticBranch({ scopeKey: actionScopeKey, branch: nextBranchName });
          setThreadBranch(nextBranchName, selectionTarget.nextWorktreePath);
          return;
        }
        setOptimisticBranch({ scopeKey: actionScopeKey, branch: previousBranch });
        if (!isAtomCommandInterrupted(checkoutResult)) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Failed to switch ref.",
              description: toBranchActionErrorMessage(squashAtomCommandFailure(checkoutResult)),
              data: { threadRef: actionOrigin.threadRef },
            }),
          );
        }
      });
    },
    [
      activeProjectCwd,
      activeWorktreePath,
      branchActionOrigin,
      branchCwd,
      environmentId,
      isActionPending,
      isSelectingWorktreeBase,
      onComposerFocusRequest,
      resolvedActiveBranch,
      runBranchAction,
      setBranchPresentationPatch,
      setThreadBranch,
      switchRef,
    ],
  );
  const createRef = useCallback(
    (rawName: string) => {
      const name = rawName.trim();
      if (!branchCwd || !name || isActionPending) return;
      const actionOrigin = branchActionOrigin;
      setBranchPresentationPatch({ menuOpen: false, menuOwner: null });
      onComposerFocusRequest?.();
      runBranchAction(async (actionScopeKey) => {
        const previousBranch = resolvedActiveBranch;
        setOptimisticBranch({ scopeKey: actionScopeKey, branch: name });
        const createBranchResult = await createRefMutation({
          environmentId,
          input: {
            cwd: branchCwd,
            refName: name,
            switchRef: true,
          },
        });
        if (createBranchResult._tag === "Success") {
          setOptimisticBranch({
            scopeKey: actionScopeKey,
            branch: createBranchResult.value.refName,
          });
          setThreadBranch(createBranchResult.value.refName, activeWorktreePath);
          return;
        }
        setOptimisticBranch({ scopeKey: actionScopeKey, branch: previousBranch });
        if (!isAtomCommandInterrupted(createBranchResult)) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Failed to create and switch ref.",
              description: toBranchActionErrorMessage(squashAtomCommandFailure(createBranchResult)),
              data: { threadRef: actionOrigin.threadRef },
            }),
          );
        }
      });
    },
    [
      activeWorktreePath,
      branchActionOrigin,
      branchCwd,
      createRefMutation,
      environmentId,
      isActionPending,
      onComposerFocusRequest,
      resolvedActiveBranch,
      runBranchAction,
      setBranchPresentationPatch,
      setThreadBranch,
    ],
  );
  const defaultBranchName = useMemo(
    () => refs.find((refName) => refName.isDefault)?.name ?? null,
    [refs],
  );
  const isInitialLoadPending = branchRefState.isPending && branchRefState.data === null;
  const worktreeBaseBranchCandidate = isInitialLoadPending
    ? null
    : (defaultBranchName ?? currentGitBranch);
  useEffect(() => {
    if (
      effectiveEnvMode !== "worktree" ||
      activeWorktreePath ||
      activeThreadBranch ||
      !worktreeBaseBranchCandidate
    ) {
      return;
    }
    setThreadBranch(worktreeBaseBranchCandidate, null);
  }, [
    activeThreadBranch,
    activeWorktreePath,
    effectiveEnvMode,
    setThreadBranch,
    worktreeBaseBranchCandidate,
  ]);
  const sourceControlPresentation = useMemo(
    () => getSourceControlPresentation(branchStatusQuery.data?.sourceControlProvider),
    [branchStatusQuery.data?.sourceControlProvider],
  );
  const triggerLabel = resolveBranchTriggerLabel({
    activeWorktreePath,
    effectiveEnvMode,
    resolvedActiveBranch,
    resolvedActiveBranchIsRemote,
    startFromOrigin,
  });
  const branchPr = resolveThreadPr({
    threadBranch: resolveBranchToolbarPrBranch({
      activeThreadBranch,
      resolvedActiveBranch,
    }),
    gitStatus: branchStatusQuery.data ?? null,
  });
  const branchPrStatus = prStatusIndicator(branchPr, branchStatusQuery.data?.sourceControlProvider);
  const branchPrTooltip = branchPr
    ? `Open ${sourceControlPresentation.terminology.singular} #${branchPr.number} (${branchPr.state}) in browser`
    : "";
  const isFetchingNextPage = branchRefState.isFetchingNextPage;
  const totalBranchCount = branchRefState.data?.totalCount ?? 0;
  const branchStatusText = isInitialLoadPending
    ? "Loading refs..."
    : isFetchingNextPage
      ? "Loading more refs..."
      : hasNextPage
        ? `Showing ${refs.length} of ${totalBranchCount} refs`
        : null;
  const requestCheckoutPullRequest = useCallback(() => {
    if (!prReference || !onCheckoutPullRequestRequest) return;
    setBranchPresentationPatch({ menuOpen: false, menuOwner: null, query: "" });
    onComposerFocusRequest?.();
    onCheckoutPullRequestRequest(prReference);
  }, [
    onCheckoutPullRequestRequest,
    onComposerFocusRequest,
    prReference,
    setBranchPresentationPatch,
  ]);

  return useMemo(
    () => ({
      activeWorktreePath,
      availableEnvironments,
      branch: {
        activeProjectCwd,
        branchPr,
        branchPrStatus,
        branchPrTooltip,
        branchQuery: activeBranchPresentationState.query,
        branchStatusText,
        branchByName,
        branchPickerItems,
        checkoutPullRequestItemValue,
        createBranchItemValue,
        filteredBranchPickerItems,
        hasNextPage,
        isActionPending,
        isFetchingNextPage,
        isInitialLoadPending,
        isMenuOpen: activeBranchPresentationState.menuOpen,
        menuOwner: activeBranchPresentationState.menuOwner,
        isSelectingWorktreeBase,
        prReference,
        resolvedActiveBranch,
        sourceControlPresentation,
        startFromOriginSwitchId,
        triggerLabel,
        createRef,
        loadNextPage: branchRefState.loadNext,
        requestCheckoutPullRequest,
        selectRef,
        setBranchQuery: (query: string) => setBranchPresentationPatch({ query }),
        setMenuOpen: (owner: string, open: boolean) => {
          setBranchPresentationState((current) => {
            const scoped =
              current.scopeKey === controllerScopeKey
                ? current
                : {
                    scopeKey: controllerScopeKey,
                    menuOpen: false,
                    menuOwner: null,
                    query: "",
                  };
            if (!open && scoped.menuOwner !== owner) {
              return scoped;
            }
            return {
              ...scoped,
              menuOpen: open,
              menuOwner: open ? owner : null,
              ...(open ? {} : { query: "" }),
            };
          });
        },
      },
      effectiveEnvMode,
      environmentId,
      envLocked,
      envModeLocked,
      hasActiveContext: (serverThread !== null || draftThread !== null) && activeProject !== null,
      previousWorktreeLabel,
      showEnvironmentIndicator,
      showEnvironmentPicker,
      startFromOrigin,
      activeThreadBranch,
      onEnvironmentChange,
      onEnvModeChange,
      onStartFromOriginChange,
      onUsePreviousWorktree,
    }),
    [
      activeBranchPresentationState.menuOpen,
      activeBranchPresentationState.menuOwner,
      activeBranchPresentationState.query,
      activeProject,
      activeProjectCwd,
      activeThreadBranch,
      activeWorktreePath,
      availableEnvironments,
      branchByName,
      branchPickerItems,
      branchPr,
      branchPrStatus,
      branchPrTooltip,
      branchRefState.loadNext,
      branchStatusText,
      checkoutPullRequestItemValue,
      createBranchItemValue,
      createRef,
      draftThread,
      effectiveEnvMode,
      envLocked,
      envModeLocked,
      environmentId,
      filteredBranchPickerItems,
      hasNextPage,
      isActionPending,
      isFetchingNextPage,
      isInitialLoadPending,
      isSelectingWorktreeBase,
      onEnvironmentChange,
      onEnvModeChange,
      onStartFromOriginChange,
      onUsePreviousWorktree,
      previousWorktreeLabel,
      prReference,
      requestCheckoutPullRequest,
      resolvedActiveBranch,
      selectRef,
      serverThread,
      setBranchPresentationPatch,
      showEnvironmentIndicator,
      showEnvironmentPicker,
      sourceControlPresentation,
      startFromOrigin,
      startFromOriginSwitchId,
      triggerLabel,
    ],
  );
}

export function RunContextControllerProvider({
  children,
  ...options
}: RunContextControllerOptions & { readonly children: ReactNode }) {
  const controller = useCreateRunContextController(options);
  return (
    <RunContextControllerContext.Provider value={controller}>
      {children}
    </RunContextControllerContext.Provider>
  );
}

export function useRunContextController(): RunContextController {
  const controller = useContext(RunContextControllerContext);
  if (!controller) {
    throw new Error("Run-context consumers must be rendered inside its controller provider.");
  }
  return controller;
}
